import { format } from "date-fns";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Chat } from "snudbud-backbone-sdk";
import Footer from "../components/layout/Footer";
import Header from "../components/layout/Header";
import { useAPI } from "../contexts/APIContext";
import usePagination from "../utils/usePagination";

export default function ChatsPage() {
  const { api, user } = useAPI();
  const [searchQuery, setSearchQuery] = useState("");
  const itemsPerPage = 10;

  // Use pagination hook to load chats
  const chatsPagination = usePagination<Chat.Chat>({
    fn: async ({ offset, limit }) => {
      const result = await api.chat.list({
        offset,
        limit: limit || itemsPerPage,
        query: searchQuery.trim() || undefined,
      });

      return { count: result.count, items: result.chats };
    },
    initial: true,
    itemsPerPage,
  });

  // Refresh chat list when search query changes
  useEffect(() => {
    chatsPagination.refresh();
  }, [searchQuery]);

  // Format chat time
  const formatMessageTime = (date: Date | undefined) => {
    if (!date) return "";

    const now = new Date();
    const messageDate = new Date(date);

    // If message is from today, show time
    if (messageDate.toDateString() === now.toDateString()) {
      return format(messageDate, "h:mm a");
    }

    // If message is from within a week, show day of week
    const oneWeekAgo = new Date(now);
    oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
    if (messageDate > oneWeekAgo) {
      return format(messageDate, "EEE");
    }

    // Otherwise, show date
    return format(messageDate, "MM/dd/yy");
  };

  // Get preview text for last message
  const getMessagePreview = (content: string | undefined) => {
    if (!content) return "No messages yet";
    return content.length > 40 ? `${content.substring(0, 40)}...` : content;
  };

  // Handle search input change
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Header />

      <main className="flex-1 w-full max-w-screen-md mx-auto sm:px-4 sm:py-8">
        <div className="bg-white overflow-hidden mb-8">
          <div className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="relative w-full">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  ></path>
                </svg>
              </div>
              <input
                type="search"
                value={searchQuery}
                onChange={handleSearchChange}
                className="block w-full p-2 pl-10 text-sm border border-gray-300 rounded-lg focus:outline-none"
                placeholder="Search messages..."
              />
            </div>
          </div>

          {chatsPagination.isLoading && chatsPagination.data.length === 0 ? (
            <div className="flex justify-center items-center py-16">
              <div className="animate-pulse flex flex-col items-center">
                <svg className="w-12 h-12 mb-3 text-blue-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                  ></path>
                </svg>
                <p className="text-gray-500">Loading messages...</p>
              </div>
            </div>
          ) : chatsPagination.data.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
              <svg className="w-16 h-16 mb-6 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                ></path>
              </svg>
              <h3 className="mb-2 text-lg font-medium text-gray-800">No messages yet</h3>
              <p className="text-gray-500 mb-6 max-w-md">
                Start a conversation by visiting someone's profile and sending them a message.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100">
              {chatsPagination.data.map((chat) => {
                const { profile, lastMessage } = chat;
                const isUnread = lastMessage && lastMessage.fromUserId !== user?.userId && !lastMessage.readAt;

                return (
                  <li key={profile.userId}>
                    <Link to={`/chat/${profile.userId}`} className="flex items-start gap-4 p-4 hover:bg-gray-50">
                      <div className="relative">
                        <img
                          src={profile.profileImageUrl || "https://via.placeholder.com/48"}
                          alt={profile.name}
                          className="w-12 h-12 rounded-full object-cover"
                        />
                        {isUnread && (
                          <span className="absolute -top-1 -right-1 w-3.5 h-3.5 bg-blue-500 rounded-full border-2 border-white"></span>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-baseline">
                          <h3 className={`font-medium ${isUnread ? "text-gray-900" : "text-gray-700"}`}>
                            {profile.name}
                          </h3>
                          {lastMessage && (
                            <span className="text-xs text-gray-500">{formatMessageTime(lastMessage.createdAt)}</span>
                          )}
                        </div>
                        <p
                          className={`text-sm truncate mt-1 ${
                            isUnread ? "font-medium text-gray-900" : "text-gray-500"
                          }`}
                        >
                          {lastMessage?.audioUrl ? (
                            <span className="italic">Audio</span>
                          ) : (
                            getMessagePreview(lastMessage?.content || undefined)
                          )}
                        </p>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}

          {chatsPagination.isLoading && chatsPagination.data.length > 0 && (
            <div className="flex justify-center py-4">
              <div className="inline-flex items-center text-gray-500">
                <svg
                  className="animate-spin -ml-1 mr-2 h-5 w-5 text-blue-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Loading more...
              </div>
            </div>
          )}

          {chatsPagination.count > chatsPagination.data.length && !chatsPagination.isLoading && (
            <div className="flex justify-center p-4">
              <button
                onClick={() => chatsPagination.load({ append: true })}
                className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
              >
                Load more messages
              </button>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
